<script setup lang="ts">
import DonateImpactCarte from "./DonateImpactCarte.vue";
import { useCarrouselPaliers } from "./useCarrouselPaliers";

// Montants d'impact : cartes « image + titre + description + montant » placées
// sous les montants classiques. Un clic sélectionne le montant, comme un
// bouton de montant ; elles montrent concrètement ce que permet le don.
export interface ImpactOption {
  amount: number;
  /** Titre court (« Un repas chaud »). */
  label: string;
  description?: string;
  /** URL absolue, chemin public (/…) ou identifiant de fichier Directus. */
  image?: string;
}

const props = withDefaults(defineProps<{
  options: ImpactOption[];
  amount: string;
  formatAmount: (amount: number) => string;
  primaryColor?: string | null;
  /** Don récurrent : période affichée après le montant (« mois » → « 20 € / mois »). */
  periodSuffix?: string | null;
  /** Titre « Ce que permet votre don » affiché par le parent (carrousel). */
  masquerTitre?: boolean;
}>(), {
  primaryColor: null,
  periodSuffix: null,
  masquerTitre: false,
});

const emit = defineEmits<{
  "update:amount": [value: string];
}>();

const accent = computed(() => props.primaryColor || "#111827");
const isSelected = (option: ImpactOption) => parseFloat(props.amount) === option.amount;

// ─── Plus de 2 paliers : carrousel (même mécanique que la page cagnotte) ─
const enCarrousel = computed(() => props.options.length > 2);
const {
  indexPalier,
  rail,
  anime,
  enGlisse,
  styleRail,
  changerPalier,
  finAnimation,
  debutGlisse,
  pendantGlisse,
  finGlisse,
  bloquerClicApresGlisse,
  roue,
  allerA,
} = useCarrouselPaliers(toRef(props, "options"));

/**
 * Montant saisi (bouton rapide, champ libre, lien `?montant=`) égal à un
 * palier : le carrousel glisse jusqu'à lui (Karim 2026-10-13). À l'ouverture,
 * il s'y place directement. Petit délai : pendant qu'on tape « 100 », on ne
 * s'arrête pas sur le palier de 10 €.
 */
let delaiMontant: ReturnType<typeof setTimeout> | null = null;
let premierMontant = true;
watch(
  () => [props.amount, props.options.length] as const,
  ([montant]) => {
    if (!enCarrousel.value) return;
    const i = props.options.findIndex((o) => o.amount === parseFloat(montant));
    if (delaiMontant) clearTimeout(delaiMontant);
    if (i < 0) return;
    if (premierMontant) {
      premierMontant = false;
      allerA(i, false);
      return;
    }
    delaiMontant = setTimeout(() => allerA(i), 250);
  },
  { immediate: true },
);
onBeforeUnmount(() => {
  if (delaiMontant) clearTimeout(delaiMontant);
});
</script>

<template>
  <div v-if="options.length" class="space-y-2.5" data-preview-id="impact-options">
    <div v-if="!masquerTitre || enCarrousel" class="flex items-center justify-between gap-3">
      <!-- Une seule ligne dans toutes les langues (titre coupé au besoin). -->
      <p v-if="!masquerTitre" class="flex min-w-0 items-center gap-1.5 text-sm font-semibold text-gray-900">
        <UIcon name="i-lucide-heart-handshake" class="size-4 shrink-0" :style="{ color: accent }" />
        <span class="truncate">{{ $t("don.impact.titre") }}</span>
      </p>
      <span v-else />
      <div v-if="enCarrousel" class="flex shrink-0 items-center gap-1.5">
        <span class="mr-1 whitespace-nowrap text-xs tabular-nums text-gray-400">{{ indexPalier + 1 }} / {{ options.length }}</span>
        <button
          type="button"
          class="flex size-8 items-center justify-center rounded-full bg-white text-gray-900 ring-1 ring-gray-200 transition-all hover:ring-gray-900"
          :aria-label="$t('don.impact.precedent')"
          @click="changerPalier(-1)"
        >
          <UIcon name="i-lucide-chevron-left" class="size-4" />
        </button>
        <button
          type="button"
          class="flex size-8 items-center justify-center rounded-full bg-white text-gray-900 ring-1 ring-gray-200 transition-all hover:ring-gray-900"
          :aria-label="$t('don.impact.suivant')"
          @click="changerPalier(1)"
        >
          <UIcon name="i-lucide-chevron-right" class="size-4" />
        </button>
      </div>
    </div>
    <!-- Plus de 2 paliers : un à la fois, en carrousel (flèches, glisser,
         boucle) comme sur la page cagnotte (Karim 2026-10-13). Sinon : la
         liste. pt/-mt : la coche d'une carte sans image dépasse en haut. -->
    <div
      v-if="enCarrousel"
      class="overflow-hidden pt-2.5 -mt-2.5 select-none"
      :class="enGlisse ? 'cursor-grabbing' : 'cursor-grab'"
      style="touch-action: pan-y"
      @pointerdown="debutGlisse"
      @pointermove="pendantGlisse"
      @pointerup="finGlisse"
      @pointercancel="finGlisse"
      @click.capture="bloquerClicApresGlisse"
      @wheel="roue"
    >
      <div
        class="flex items-start"
        :class="anime ? 'transition-transform duration-300 ease-out' : ''"
        :style="styleRail"
        @transitionend.self="finAnimation"
      >
        <div v-for="diapo in rail" :key="diapo.cle" class="w-full shrink-0">
          <DonateImpactCarte
            :option="diapo.palier"
            :selectionne="isSelected(diapo.palier)"
            :accent="accent"
            :format-amount="formatAmount"
            :period-suffix="periodSuffix"
            @choisir="emit('update:amount', String(diapo.palier.amount))"
          />
        </div>
      </div>
    </div>
    <DonateImpactCarte
      v-for="option in enCarrousel ? [] : options"
      :key="`${option.amount}-${option.label}`"
      :option="option"
      :selectionne="isSelected(option)"
      :accent="accent"
      :format-amount="formatAmount"
      :period-suffix="periodSuffix"
      @choisir="emit('update:amount', String(option.amount))"
    />
  </div>
</template>
